'use client'

import {
  BookOpen,
  CheckCircle,
  Clock,
  LoaderCircle,
  X,
  Zap,
} from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useCallback, useRef, useState, useTransition } from 'react'
import { toast } from 'sonner'
import { checkAuthStatusAction } from '@/actions/auth/check-auth-status-action'
import { sendEventBrevo } from '@/actions/send-event-brevo'
import { addToCartAction } from '@/actions/shopping/add-to-cart-action'
import { getProfileAction } from '@/actions/user/get-profile-action'
import { createEnrolmentAction } from '@/app/(common)/cursos/info/_actions/create-enrolment-action'
import { checkExistEnrolment } from '@/app/(common)/cursos/info/_actions/exist-enrolment-action'
import { trackEvent } from '@/components/@analitycs/trackEvent'
import ModalLayout from '@/components/enrolment-enter-modal/modal-layout'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { getActiveWorkloads } from '@/helpers/active-workloads'
import type { GetProductOutput } from '@/http/zamply/products/get-product-schema'
import { getBrevoProductProperties } from '@/utils/brevo-product-type'

interface SubscriptionButtonProps {
  product: GetProductOutput
  variant?: 'primary' | 'secondary'
}

interface WorkloadButtonProps {
  workload: {
    reference: string
    workload_value: number
  }
  index: number
  isSelected: boolean
  isProcessing: boolean
  isPending: boolean
  onCreateEnrolment: (workload: {
    workload_value: number
    reference: string
  }) => void
}

const WorkloadButton = ({
  workload,
  index,
  isSelected,
  isProcessing,
  isPending,
  onCreateEnrolment,
}: WorkloadButtonProps) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault()
      const nextButton = (
        e.currentTarget as HTMLElement
      ).parentElement?.nextElementSibling?.querySelector(
        'button'
      ) as HTMLButtonElement
      nextButton?.focus()
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault()
      const prevButton = (
        e.currentTarget as HTMLElement
      ).parentElement?.previousElementSibling?.querySelector(
        'button'
      ) as HTMLButtonElement
      prevButton?.focus()
    }
  }

  return (
    <div
      className={`group relative transition-all duration-300 ${
        isSelected ? 'z-10' : 'hover:scale-[1.01]'
      }`}
    >
      <div className="relative">
        <button
          aria-checked={isSelected}
          aria-describedby={`workload-${workload.reference}`}
          className={`h-20 w-full overflow-hidden rounded-2xl border-2 p-4 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-primary-500/20 sm:h-28 sm:p-6 ${
            isSelected
              ? 'border-primary-500 bg-gradient-to-br from-primary-50 to-primary-50 shadow-lg shadow-primary-500/20'
              : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-md hover:shadow-primary-500/10'
          } ${isPending ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          disabled={isPending}
          onClick={() => onCreateEnrolment(workload)}
          onKeyDown={handleKeyDown}
          role="radio"
          tabIndex={index === 0 ? 0 : -1}
          type="button"
        >
          <div className="flex h-full flex-col items-center justify-center gap-2 sm:gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-300 sm:h-12 sm:w-12 sm:rounded-2xl ${
                isSelected
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 group-hover:bg-primary-100 group-hover:text-primary-600'
              }`}
            >
              {isProcessing ? (
                <LoaderCircle className="h-5 w-5 animate-spin sm:h-6 sm:w-6" />
              ) : (
                <Clock className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </div>
            <span
              className={`font-bold text-sm transition-colors duration-200 sm:text-base ${
                isSelected
                  ? 'text-primary-700'
                  : 'text-gray-700 group-hover:text-primary-600'
              }`}
            >
              {workload.workload_value} Horas
            </span>
          </div>

          {isSelected && (
            <div
              aria-hidden="true"
              className="-right-2 -top-2 zoom-in-95 absolute flex h-7 w-7 animate-in items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg duration-300 sm:h-8 sm:w-8"
            >
              <CheckCircle className="h-3.5 w-3.5 text-white sm:h-4 sm:w-4" />
            </div>
          )}

          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500/0 via-primary-500/0 to-primary-500/0 opacity-0 transition-opacity duration-300 group-hover:via-primary-500/5 group-hover:opacity-100" />
        </button>
      </div>

      <div className="sr-only" id={`workload-${workload.reference}`}>
        Opção de {workload.workload_value} horas de carga horária.
        {isSelected ? 'Selecionado.' : 'Não selecionado.'}
        {isProcessing ? 'Processando inscrição...' : ''}
      </div>
    </div>
  )
}

export default function SubscriptionButton({
  product,
  variant = 'primary',
}: SubscriptionButtonProps) {
  const isSubscriptionFree =
    product.billing_type === 'postpaid' || product.billing_type === 'free'
  const activeWorkloads = getActiveWorkloads(product.workloads)
  const [shouldShowFormAuth, setShouldShowFormAuth] = useState(false)
  const [shouldShowDialogWorkloads, setShouldShowDialogWorkloads] =
    useState(false)
  const [enrolmentData, setEnrolmentData] = useState<{
    product_id: string
    product_external_id: number
    workload_value: number
    workload_id: string
    period_id: string
  } | null>(null)

  const [isPending, startTransition] = useTransition()
  const [selectedWorkload, setSelectedWorkload] = useState<string | null>(null)
  const isCreatingEnrolmentRef = useRef(false)

  const router = useRouter()
  const isTrack = product.product_type?.toLowerCase() === 'track'
  let ctaLabel = 'INSCREVA-SE'
  if (isTrack) {
    ctaLabel = 'Adquirir agora mesmo'
  } else if (isSubscriptionFree) {
    ctaLabel = 'INSCREVA-SE GRÁTIS'
  }
  const checkoutUrl = '/checkout/payment/resume'
  const classroomUrl = `/app/sala/curso/${product.external_id ?? product.reference}`
  const singleWorkload =
    activeWorkloads.length === 1 ? activeWorkloads[0] : null

  const createEnrolment = useCallback(
    async (workload: { workload_value: number; reference: string }) => {
      // Evita disparos duplicados de matrícula/tracking em caso de clique
      // duplo ou reentrada concorrente antes de isPending atualizar.
      if (isCreatingEnrolmentRef.current) {
        return
      }
      isCreatingEnrolmentRef.current = true

      setSelectedWorkload(workload.reference)
      const payload = {
        product_id: product.reference,
        product_external_id: Number(product.external_id),
        workload_value: Number(workload.workload_value),
        workload_id: workload.reference,
        period_id: product.periods[0].reference,
      }

      setEnrolmentData(payload)

      let resolvedAuth: 'authenticated' | 'unauthenticated'
      try {
        resolvedAuth = await checkAuthStatusAction()
      } catch {
        resolvedAuth = 'unauthenticated'
      }

      if (resolvedAuth === 'unauthenticated') {
        isCreatingEnrolmentRef.current = false
        setShouldShowFormAuth(true)
        setShouldShowDialogWorkloads(false)
        setSelectedWorkload(null)
        return
      }

      try {
        const [resultAction, profileAction] = await Promise.all([
          createEnrolmentAction({
            period_id: payload.period_id,
            product_id: payload.product_id,
            workload_id: payload.workload_id,
            product_external_id: product.external_id,
          }),
          getProfileAction(),
        ])

        const enrolmentResult = resultAction.data
        const enrolment = enrolmentResult?.data

        if (resultAction.serverError || enrolmentResult?.error || !enrolment) {
          toast.error('Erro ao realizar inscrição', {
            description: 'Tente novamente em alguns instantes',
          })
          return
        }

        // Trilha: criar → adicionar ao carrinho → checkout
        if (isTrack && !('cart' in enrolment && enrolment.cart)) {
          const cartResult = await addToCartAction({
            enrolment_id: enrolment.id,
            item_type: 'access',
          })

          if (
            cartResult?.serverError ||
            cartResult?.data?.error ||
            !cartResult?.data?.data
          ) {
            toast.error('Erro ao adicionar ao carrinho', {
              description: 'Tente novamente em alguns instantes',
            })
            return
          }
        }

        const profile = profileAction.data?.data
        void Promise.all([
          sendEventBrevo({
            event: 'course_inscription',
            properties: {
              course_name: product.name,
              course_id: product.reference,
              ...getBrevoProductProperties(product.product_type),
            },
            eventdata: {
              data: {
                course_name: product.name,
                course_id: product.reference,
                ...getBrevoProductProperties(product.product_type),
                matricula_id: enrolment.id,
              },
            },
          }),
          trackEvent({
            event: 'vitrine',
            eventName: 'subscription',
            customDataObject: {
              environment: 'Vitrine',
              value: enrolment.product?.amount,
              subscription_id: enrolment.id,
              external_id: profile?.external_id ?? profile?.reference,
            },
            dedupeId: enrolment.id,
          }),
        ]).catch(() => {
          // Analytics não deve bloquear o redirecionamento
        })

        toast.success('Inscrição realizada com sucesso!', {
          description: 'Aguarde o redirecionamento',
        })

        if (isTrack) {
          router.prefetch(checkoutUrl)
          router.push(checkoutUrl)
          return
        }

        // Navegação completa: um router.push logo após a server action (que
        // expira o cache da matrícula) podia ser descartado pelo refresh da
        // própria action, e o usuário continuava vendo o pedido de inscrição
        // — principalmente no primeiro acesso, quando a sessão foi criada
        // nesta mesma navegação. O hard navigation garante que a sala seja
        // renderizada do zero já reconhecendo a nova matrícula.
        window.location.assign(classroomUrl)
      } catch {
        toast.error('Erro ao realizar inscrição', {
          description: 'Tente novamente em alguns instantes',
        })
      } finally {
        isCreatingEnrolmentRef.current = false
        setSelectedWorkload(null)
        setShouldShowDialogWorkloads(false)
      }
    },
    [checkoutUrl, classroomUrl, isTrack, product, router]
  )

  const handleCreateEnrolment = useCallback(
    (workload: { workload_value: number; reference: string }) => {
      startTransition(async () => {
        await createEnrolment(workload)
      })
    },
    [createEnrolment]
  )

  const handleCheckExistEnrolment = useCallback(() => {
    startTransition(async () => {
      const enrolmentExists = await checkExistEnrolment({
        reference: product.reference,
      })

      if (!enrolmentExists.data) {
        toast.error('Erro ao verificar inscrição', {
          description: 'Tente novamente em alguns instantes',
        })
        return
      }

      if (enrolmentExists.data.success) {
        router.push(classroomUrl)
        return
      }

      if (singleWorkload) {
        await createEnrolment(singleWorkload)
        return
      }

      setShouldShowDialogWorkloads(true)
    })
  }, [classroomUrl, createEnrolment, product.reference, router, singleWorkload])

  return (
    <>
      <AlertDialog
        onOpenChange={setShouldShowDialogWorkloads}
        open={shouldShowDialogWorkloads}
      >
        {variant === 'primary' ? (
          <Button
            aria-describedby={isPending ? 'loading-enrollment' : undefined}
            className="group relative mt-3 min-h-12 bg-gradient-to-r from-emerald-600 to-emerald-700 px-8 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-[1.02] hover:from-emerald-700 hover:to-emerald-800 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={isPending}
            onClick={handleCheckExistEnrolment}
          >
            {isPending ? (
              <>
                <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                <span className="sr-only" id="loading-enrollment">
                  Carregando inscrição
                </span>
                Carregando...
              </>
            ) : (
              <>
                <Zap className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                {ctaLabel}
              </>
            )}
          </Button>
        ) : (
          <button
            className="group relative font-medium text-primary-700 transition-all duration-200 hover:text-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isPending}
            onClick={handleCheckExistEnrolment}
            type="button"
          >
            <span className="relative">
              {isPending ? 'Carregando...' : 'Clique aqui.'}
              <span className="-bottom-1 absolute left-0 h-0.5 w-0 bg-primary-600 transition-all duration-300 group-hover:w-full" />
            </span>
          </button>
        )}

        <AlertDialogContent className="data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 flex max-h-[90dvh] w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-3xl border-0 bg-white p-0 shadow-2xl data-[state=closed]:animate-out data-[state=open]:animate-in sm:max-w-2xl">
          {/* Header with gradient background */}
          <AlertDialogHeader className="relative shrink-0 overflow-hidden bg-gradient-to-br from-primary-600 via-primary-600 to-primary-700 px-5 py-4 text-white sm:px-8 sm:py-6">
            <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent" />
            <div className="relative flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/20 shadow-lg backdrop-blur-sm sm:h-14 sm:w-14">
                  <BookOpen className="h-5 w-5 text-white sm:h-7 sm:w-7" />
                </div>
                <div className="min-w-0">
                  <AlertDialogTitle className="font-bold text-lg sm:text-xl md:text-2xl">
                    Inscrição no Curso
                  </AlertDialogTitle>
                  <p className="mt-0.5 truncate font-medium text-primary-100 text-xs opacity-90 sm:mt-1 sm:text-sm">
                    {product.name}
                  </p>
                </div>
              </div>
              <AlertDialogCancel
                aria-label="Fechar modal de inscrição"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/20 text-white transition-all duration-200 hover:scale-110 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 sm:h-12 sm:w-12"
                onClick={() => setShouldShowDialogWorkloads(false)}
              >
                <X className="h-10 w-10" />
              </AlertDialogCancel>
            </div>
          </AlertDialogHeader>

          {/* Scrollable content */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-5 pb-4 sm:px-8 sm:pt-6 sm:pb-6">
            {/* Section header */}
            <div className="mb-5 text-center sm:mb-8">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-50 to-primary-50 shadow-inner sm:mb-6 sm:h-20 sm:w-20 sm:rounded-3xl">
                <Clock className="h-7 w-7 text-primary-600 sm:h-10 sm:w-10" />
              </div>
              <AlertDialogDescription className="mb-2 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text font-bold text-2xl text-transparent sm:mb-4 sm:text-3xl md:text-4xl">
                Escolha a Carga Horária
              </AlertDialogDescription>
              <p className="mx-auto max-w-md text-gray-600 text-sm leading-relaxed sm:text-lg">
                Selecione a carga horária que melhor se adapta ao seu ritmo de
                estudos
              </p>
            </div>

            {/* Workload selection grid */}
            <div
              aria-labelledby="workload-options"
              aria-required="true"
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3"
              role="radiogroup"
            >
              <div className="sr-only" id="workload-options">
                Opções de carga horária disponíveis. Use as setas para navegar e
                Enter para selecionar.
              </div>
              {activeWorkloads.map((workload, index) => {
                const isSelected = selectedWorkload === workload.reference
                const isProcessing = isPending && isSelected
                return (
                  <WorkloadButton
                    index={index}
                    isPending={isPending}
                    isProcessing={isProcessing}
                    isSelected={isSelected}
                    key={workload.reference}
                    onCreateEnrolment={handleCreateEnrolment}
                    workload={workload}
                  />
                )
              })}
            </div>

            {/* Loading state */}
            {isPending && (
              <div
                aria-live="assertive"
                className="mt-6 text-center sm:mt-8"
                role="status"
              >
                <div className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-primary-50 to-primary-50 px-6 py-3 shadow-inner">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-300 border-t-primary-600" />
                  <span className="font-semibold text-base text-primary-700">
                    Processando sua inscrição...
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Sticky footer */}
          <div className="shrink-0 border-gray-100 border-t bg-gradient-to-r from-gray-50 to-primary-50/50 px-5 py-3 sm:px-8 sm:py-4">
            <div className="flex items-center justify-between gap-3 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                <div className="h-2 w-2 shrink-0 rounded-full bg-primary-500" />
                <span className="truncate font-medium text-gray-700">
                  {product.name}
                </span>
              </div>
              <div className="shrink-0 rounded-full bg-primary-100 px-3 py-1">
                <span className="font-semibold text-primary-700 text-xs">
                  {activeWorkloads.length === 1
                    ? '1 opção disponível'
                    : `${activeWorkloads.length} opções disponíveis`}
                </span>
              </div>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {shouldShowFormAuth && enrolmentData && (
        <ModalLayout
          enrolmentData={enrolmentData}
          externalIdProduct={product.external_id}
          notLoggedIn={shouldShowFormAuth}
          productType={product.product_type}
          referenceProduct={product.reference}
          setNotLoggedIn={() => {
            setShouldShowFormAuth(false)
            setEnrolmentData(null)
            setSelectedWorkload(null)
          }}
          setShouldShowFormAuth={setShouldShowFormAuth}
        />
      )}
    </>
  )
}
